import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as authApi from "auth/authApi";
import { AuthSession, sessionFromTokenResponse } from "auth/session";

/**
 * Auth state. Reducers stay pure: saving the session to storage, refreshing
 * tokens and resetting the GraphQL cache all happen in `auth/setupAuth.ts`,
 * which reacts to these actions/state changes.
 */

export const SESSION_EXPIRED_MESSAGE =
  "Your session has expired. Please log in again.";

export interface AuthState {
  session: AuthSession | null;
  /** True once the stored session has been checked on startup. */
  initialized: boolean;
  loginError: string | null;
  loginModalOpen: boolean;
  /** Informational message shown above the login form (e.g. session expired). */
  notice: string | null;
}

const initialState: AuthState = {
  session: null,
  initialized: false,
  loginError: null,
  loginModalOpen: false,
  notice: null,
};

export const login = createAsyncThunk<
  AuthSession,
  { email: string; password: string },
  { rejectValue: string }
>("auth/login", async ({ email, password }, { rejectWithValue }) => {
  try {
    const tokens = await authApi.login(email.trim(), password);
    return sessionFromTokenResponse(tokens);
  } catch (error) {
    return rejectWithValue(authApi.describeAuthError(error));
  }
});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /** A session was read from storage (startup, or another tab changed it). */
    sessionLoaded: (state, action: PayloadAction<AuthSession | null>) => {
      state.session = action.payload;
    },
    authInitialized: (state) => {
      state.initialized = true;
    },
    sessionRefreshed: (state, action: PayloadAction<AuthSession>) => {
      state.session = action.payload;
    },
    /** The refresh token was rejected: log out and ask the user to log in again. */
    sessionExpired: (state) => {
      state.session = null;
      state.notice = SESSION_EXPIRED_MESSAGE;
      state.loginError = null;
      state.loginModalOpen = true;
    },
    logout: (state) => {
      state.session = null;
      state.notice = null;
      state.loginError = null;
    },
    openLoginModal: (state) => {
      state.loginModalOpen = true;
    },
    closeLoginModal: (state) => {
      state.loginModalOpen = false;
      state.notice = null;
      state.loginError = null;
    },
    clearLoginError: (state) => {
      state.loginError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loginError = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.session = action.payload;
        state.loginError = null;
        state.notice = null;
        state.loginModalOpen = false;
      })
      .addCase(login.rejected, (state, action) => {
        state.loginError =
          action.payload ?? "Something went wrong while logging in.";
      });
  },
});

export const {
  sessionLoaded,
  authInitialized,
  sessionRefreshed,
  sessionExpired,
  logout,
  openLoginModal,
  closeLoginModal,
  clearLoginError,
} = authSlice.actions;
export default authSlice.reducer;
